'use client';

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// ============================================
// TabsList Variants
// ============================================

const tabsListVariants = cva(
  'group/tabs-list inline-flex w-fit items-center justify-center group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col',
  {
    variants: {
      variant: {
        // Primary variants
        boxed:
          'bg-muted rounded-lg p-[3px] overflow-clip group-data-[orientation=vertical]/tabs:gap-1',
        pill: 'bg-muted rounded-full p-[3px] overflow-clip group-data-[orientation=vertical]/tabs:gap-1',
        bordered:
          'gap-3 bg-transparent relative pb-0 w-full rounded-none overflow-x-auto after:absolute after:bottom-0 after:inset-x-0 after:h-px after:bg-border after:z-0 group-data-[orientation=vertical]/tabs:overflow-x-visible group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:inset-x-auto group-data-[orientation=vertical]/tabs:after:left-0 group-data-[orientation=vertical]/tabs:after:w-px group-data-[orientation=vertical]/tabs:after:h-auto group-data-[orientation=vertical]/tabs:w-fit',
        lifted:
          'gap-0 bg-transparent relative w-full rounded-none overflow-x-auto after:absolute after:bottom-0 after:inset-x-0 after:h-px after:bg-border after:z-0 group-data-[orientation=vertical]/tabs:overflow-x-visible',
      },
      size: {
        sm: '',
        md: '',
        lg: '',
      },
      align: {
        start: '',
        center: '',
        end: '',
        stretch: '',
      },
      borderPosition: {
        bottom: '',
        top: '',
      },
    },
    compoundVariants: [
      // Size applies container height for boxed/pill variants
      {
        variant: 'boxed',
        size: 'sm',
        className: 'group-data-[orientation=horizontal]/tabs:h-[38px]',
      },
      {
        variant: 'boxed',
        size: 'md',
        className: 'group-data-[orientation=horizontal]/tabs:h-[42px]',
      },
      {
        variant: 'boxed',
        size: 'lg',
        className: 'group-data-[orientation=horizontal]/tabs:h-[46px]',
      },
      {
        variant: 'pill',
        size: 'sm',
        className: 'group-data-[orientation=horizontal]/tabs:h-[38px]',
      },
      {
        variant: 'pill',
        size: 'md',
        className: 'group-data-[orientation=horizontal]/tabs:h-[42px]',
      },
      {
        variant: 'pill',
        size: 'lg',
        className: 'group-data-[orientation=horizontal]/tabs:h-[46px]',
      },
      // Alignment for bordered variant
      { variant: 'bordered', align: 'start', className: 'justify-start' },
      { variant: 'bordered', align: 'center', className: 'justify-center' },
      { variant: 'bordered', align: 'end', className: 'justify-end' },
      { variant: 'bordered', align: 'stretch', className: 'justify-start' },
      // Alignment for lifted variant
      { variant: 'lifted', align: 'start', className: 'justify-start' },
      { variant: 'lifted', align: 'center', className: 'justify-center' },
      { variant: 'lifted', align: 'end', className: 'justify-end' },
      { variant: 'lifted', align: 'stretch', className: 'justify-start' },
      // Border position top for bordered/lifted
      {
        variant: 'bordered',
        borderPosition: 'top',
        className: 'after:bottom-auto after:top-0',
      },
      {
        variant: 'lifted',
        borderPosition: 'top',
        className: 'after:bottom-auto after:top-0',
      },
    ],
    defaultVariants: {
      variant: 'boxed',
      size: 'md',
      align: 'start',
      borderPosition: 'bottom',
    },
  },
);

type TabsListVariantProps = VariantProps<typeof tabsListVariants>;

interface TabsListProps extends TabsPrimitive.List.Props, TabsListVariantProps {
  /** Enable gradient styling on active tab */
  gradient?: boolean;
}

// ============================================
// TabsTrigger Variants
// ============================================

const tabsTriggerVariants = cva([
  // Base
  'relative inline-flex items-center justify-center whitespace-nowrap',
  'gap-1.5 border border-transparent font-medium',
  'transition-[color,background-color,border-color,box-shadow,opacity] duration-200 ease-smooth motion-reduce:transition-none',
  'group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start',
  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1',
  'disabled:pointer-events-none disabled:text-muted-foreground disabled:opacity-70',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  'text-foreground',

  // Size: sm
  'group-data-[size=sm]/tabs-list:h-7 group-data-[size=sm]/tabs-list:px-2 group-data-[size=sm]/tabs-list:text-sm',
  "group-data-[size=sm]/tabs-list:[&_svg:not([class*='size-'])]:size-4",
  // Size: md
  'group-data-[size=md]/tabs-list:h-[30px] group-data-[size=md]/tabs-list:px-2.5 group-data-[size=md]/tabs-list:text-sm',
  "group-data-[size=md]/tabs-list:[&_svg:not([class*='size-'])]:size-4",
  // Size: lg
  'group-data-[size=lg]/tabs-list:h-8 group-data-[size=lg]/tabs-list:px-3 group-data-[size=lg]/tabs-list:text-base',
  "group-data-[size=lg]/tabs-list:[&_svg:not([class*='size-'])]:size-5",

  // Boxed variant
  'group-data-[variant=boxed]/tabs-list:rounded-[10px] group-data-[variant=boxed]/tabs-list:flex-1',
  'group-data-[variant=boxed]/tabs-list:data-active:bg-secondary group-data-[variant=boxed]/tabs-list:data-active:shadow-sm group-data-[variant=boxed]/tabs-list:data-active:text-foreground',
  'group-data-[variant=boxed]/tabs-list:hover:bg-accent',
  'group-data-[orientation=vertical]/tabs:group-data-[variant=boxed]/tabs-list:flex-initial group-data-[orientation=vertical]/tabs:group-data-[variant=boxed]/tabs-list:justify-center',

  // Pill variant
  'group-data-[variant=pill]/tabs-list:rounded-full group-data-[variant=pill]/tabs-list:flex-1',
  'group-data-[variant=pill]/tabs-list:data-active:bg-background group-data-[variant=pill]/tabs-list:data-active:shadow-sm group-data-[variant=pill]/tabs-list:data-active:text-foreground',
  'group-data-[variant=pill]/tabs-list:hover:bg-accent',
  'dark:group-data-[variant=pill]/tabs-list:data-active:bg-accent dark:group-data-[variant=pill]/tabs-list:data-active:text-accent-foreground dark:group-data-[variant=pill]/tabs-list:data-active:border-input',
  'group-data-[orientation=vertical]/tabs:group-data-[variant=pill]/tabs-list:flex-initial group-data-[orientation=vertical]/tabs:group-data-[variant=pill]/tabs-list:justify-center',

  // Bordered variant
  'group-data-[variant=bordered]/tabs-list:bg-transparent group-data-[variant=bordered]/tabs-list:rounded-none',
  'group-data-[variant=bordered]/tabs-list:pb-2.5 group-data-[variant=bordered]/tabs-list:flex-initial',
  'group-data-[variant=bordered]/tabs-list:data-active:bg-background group-data-[variant=bordered]/tabs-list:data-active:text-primary group-data-[variant=bordered]/tabs-list:data-active:shadow-none',
  'group-data-[variant=bordered]/tabs-list:group-data-[align=stretch]/tabs-list:flex-1',
  'group-data-[variant=bordered]/tabs-list:after:bg-primary/90 group-data-[variant=bordered]/tabs-list:after:absolute group-data-[variant=bordered]/tabs-list:after:bottom-0 group-data-[variant=bordered]/tabs-list:after:inset-x-0 group-data-[variant=bordered]/tabs-list:after:h-0.5 group-data-[variant=bordered]/tabs-list:after:z-10 group-data-[variant=bordered]/tabs-list:after:opacity-0 group-data-[variant=bordered]/tabs-list:after:transition-opacity group-data-[variant=bordered]/tabs-list:after:duration-200 group-data-[variant=bordered]/tabs-list:after:ease-out',
  'group-data-[variant=bordered]/tabs-list:data-active:after:opacity-100',
  'group-data-[variant=bordered]/tabs-list:hover:after:opacity-100 group-data-[variant=bordered]/tabs-list:hover:after:bg-primary/20',
  'group-data-[variant=bordered]/tabs-list:data-active:hover:after:bg-primary/90',
  'group-data-[variant=bordered]/tabs-list:group-data-[border-position=top]/tabs-list:after:bottom-auto group-data-[variant=bordered]/tabs-list:group-data-[border-position=top]/tabs-list:after:top-0',
  'group-data-[variant=bordered]/tabs-list:group-data-[border-position=top]/tabs-list:pb-0 group-data-[variant=bordered]/tabs-list:group-data-[border-position=top]/tabs-list:pt-2.5',
  'group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:after:inset-x-auto group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:after:inset-y-0 group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:after:left-0 group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:after:w-0.5 group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:after:h-auto',
  'group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:pb-0 group-data-[orientation=vertical]/tabs:group-data-[variant=bordered]/tabs-list:pl-2.5',

  // Lifted variant — browser-tab style with curved corner connectors
  'group-data-[variant=lifted]/tabs-list:flex-initial',
  'group-data-[variant=lifted]/tabs-list:rounded-t-md',
  'group-data-[variant=lifted]/tabs-list:border-b group-data-[variant=lifted]/tabs-list:border-b-border',
  'group-data-[variant=lifted]/tabs-list:text-foreground',
  'group-data-[variant=lifted]/tabs-list:data-active:bg-background',
  'group-data-[variant=lifted]/tabs-list:data-active:text-primary',
  'group-data-[variant=lifted]/tabs-list:data-active:border group-data-[variant=lifted]/tabs-list:data-active:border-border',
  'group-data-[variant=lifted]/tabs-list:data-active:border-b-background',
  'group-data-[variant=lifted]/tabs-list:data-active:rounded-t-[10px]',
  'group-data-[variant=lifted]/tabs-list:data-active:z-10',
  'group-data-[variant=lifted]/tabs-list:data-active:before:absolute group-data-[variant=lifted]/tabs-list:data-active:before:bottom-0 group-data-[variant=lifted]/tabs-list:data-active:before:left-[-10px] group-data-[variant=lifted]/tabs-list:data-active:before:size-[10px] group-data-[variant=lifted]/tabs-list:data-active:before:rounded-br-[10px] group-data-[variant=lifted]/tabs-list:data-active:before:border-b group-data-[variant=lifted]/tabs-list:data-active:before:border-r group-data-[variant=lifted]/tabs-list:data-active:before:border-border group-data-[variant=lifted]/tabs-list:data-active:before:bg-background group-data-[variant=lifted]/tabs-list:data-active:before:shadow-[2px_2px_0_0_var(--background)]',
  'group-data-[variant=lifted]/tabs-list:data-active:after:absolute group-data-[variant=lifted]/tabs-list:data-active:after:bottom-0 group-data-[variant=lifted]/tabs-list:data-active:after:right-[-10px] group-data-[variant=lifted]/tabs-list:data-active:after:size-[10px] group-data-[variant=lifted]/tabs-list:data-active:after:rounded-bl-[10px] group-data-[variant=lifted]/tabs-list:data-active:after:border-b group-data-[variant=lifted]/tabs-list:data-active:after:border-l group-data-[variant=lifted]/tabs-list:data-active:after:border-border group-data-[variant=lifted]/tabs-list:data-active:after:bg-background group-data-[variant=lifted]/tabs-list:data-active:after:shadow-[-2px_2px_0_0_var(--background)]',
  'group-data-[variant=lifted]/tabs-list:group-data-[align=stretch]/tabs-list:flex-1',

  // Gradient support (uses brand tokens)
  'group-data-[variant=boxed]/tabs-list:group-data-[gradient]/tabs-list:data-active:bg-[linear-gradient(100deg,var(--air-100)/0.1,var(--primary)/0.1)] group-data-[variant=boxed]/tabs-list:group-data-[gradient]/tabs-list:data-active:text-primary',
  'group-data-[variant=pill]/tabs-list:group-data-[gradient]/tabs-list:data-active:bg-[linear-gradient(98deg,var(--air-100),var(--primary))] group-data-[variant=pill]/tabs-list:group-data-[gradient]/tabs-list:data-active:text-primary-foreground',

  // Hide pseudo-element underline when TabsIndicator is used
  'group-has-[[data-slot=tabs-indicator]]/tabs-list:after:hidden',
]);

// ============================================
// Components
// ============================================

const Tabs = React.forwardRef<HTMLDivElement, TabsPrimitive.Root.Props>(
  ({ className, orientation = 'horizontal', ...props }, ref) => (
    <TabsPrimitive.Root
      ref={ref}
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        'gap-2 group/tabs flex data-[orientation=horizontal]:flex-col',
        className,
      )}
      {...props}
    />
  ),
);

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  (
    {
      className,
      variant = 'boxed',
      size = 'md',
      align = 'start',
      borderPosition = 'bottom',
      gradient,
      ...props
    },
    ref,
  ) => {
    return (
      <TabsPrimitive.List
        ref={ref}
        data-slot="tabs-list"
        data-variant={variant}
        data-align={align}
        data-size={size}
        data-border-position={borderPosition}
        {...(gradient ? { 'data-gradient': '' } : {})}
        className={cn(
          tabsListVariants({ variant, size, align, borderPosition }),
          className,
        )}
        {...props}
      />
    );
  },
);

const TabsTrigger = React.forwardRef<
  HTMLButtonElement,
  TabsPrimitive.Tab.Props
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Tab
    ref={ref}
    data-slot="tabs-trigger"
    className={cn(tabsTriggerVariants(), className)}
    {...props}
  />
));

const TabsContent = React.forwardRef<HTMLDivElement, TabsPrimitive.Panel.Props>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Panel
      ref={ref}
      data-slot="tabs-content"
      className={cn(
        'flex-1 focus-visible:outline-none motion-safe:transition-opacity duration-(--duration-fast) ease-standard motion-safe:data-[starting-style]:opacity-0 data-[ending-style]:transition-none motion-reduce:transition-none',
        className,
      )}
      {...props}
    />
  ),
);

/**
 * Animated indicator that slides to the active tab position.
 * Use with `variant="bordered"` on TabsList.
 *
 * @example
 * ```tsx
 * <TabsList variant="bordered">
 *   <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *   <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   <TabsIndicator />
 * </TabsList>
 * ```
 */
const TabsIndicator = React.forwardRef<
  HTMLSpanElement,
  TabsPrimitive.Indicator.Props
>(({ className, style, ...props }, ref) => {
  const [element, setElement] = React.useState<HTMLSpanElement | null>(null);
  const mergedRef = React.useCallback(
    (node: HTMLSpanElement | null) => {
      setElement(node);
      if (typeof ref === 'function') return ref(node);
      if (ref) ref.current = node;
    },
    [ref],
  );
  React.useEffect(() => {
    const list = element?.closest('[role="tablist"]');
    const owner = element?.ownerDocument.defaultView;
    if (!element || !list || !owner?.ResizeObserver) return;
    const sizes = new Map<
      Element,
      { width: number; height: number } | undefined
    >();
    let measured = false;
    const sync = () => {
      const active = [
        ...list.querySelectorAll('[role="tab"][aria-selected="true"]'),
      ].find((tab) => tab.closest('[role="tablist"]') === list);
      const size = active && sizes.get(active);
      if (!size) return;
      const transition = element.style.transitionProperty;
      // The initial precision correction is layout, not selection travel.
      if (!measured) element.style.transitionProperty = 'none';
      for (const axis of ['width', 'height'] as const) {
        const property = `--humain-active-tab-${axis}`;
        const value = `${size[axis]}px`;
        if (element.style.getPropertyValue(property) !== value)
          element.style.setProperty(property, value);
      }
      if (!measured) {
        element.getBoundingClientRect();
        element.style.transitionProperty = transition;
        measured = true;
      }
    };
    // Computed CSS dimensions lose decimal precision when serialized; the
    // observed border box preserves layout units without including transforms.
    const resize = new owner.ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.borderBoxSize[0];
        if (!box) continue;
        const vertical = !owner
          .getComputedStyle(entry.target)
          .writingMode.startsWith('horizontal');
        sizes.set(entry.target, {
          width: vertical ? box.blockSize : box.inlineSize,
          height: vertical ? box.inlineSize : box.blockSize,
        });
      }
      sync();
    });
    const observe = () => {
      for (const tab of sizes.keys()) {
        if (!list.contains(tab) || tab.closest('[role="tablist"]') !== list) {
          resize.unobserve(tab);
          sizes.delete(tab);
        }
      }
      for (const tab of list.querySelectorAll('[role="tab"]')) {
        if (tab.closest('[role="tablist"]') === list && !sizes.has(tab)) {
          sizes.set(tab, undefined);
          resize.observe(tab, { box: 'border-box' });
        }
      }
      sync();
    };
    const mutation = new owner.MutationObserver(observe);
    mutation.observe(list, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-selected', 'style'],
    });
    observe();
    return () => {
      resize.disconnect();
      mutation.disconnect();
    };
  }, [element]);
  return (
    <TabsPrimitive.Indicator
      ref={mergedRef}
      data-slot="tabs-indicator"
      className={cn(
        'absolute bottom-0 left-0 h-0.5 z-10 bg-primary/90',
        'w-[var(--active-tab-width)] translate-x-[var(--active-tab-left,0px)]',
        'data-[orientation=vertical]:top-0 data-[orientation=vertical]:bottom-auto data-[orientation=vertical]:w-0.5 data-[orientation=vertical]:h-[var(--active-tab-height)] data-[orientation=vertical]:translate-x-0 data-[orientation=vertical]:translate-y-[var(--active-tab-top,0px)]',
        'transition-[translate,width,height] duration-(--duration-moderate) ease-standard motion-reduce:transition-none',
        className,
      )}
      {...props}
      style={(state) => ({
        // Base UI owns selection and position; only restore border-box precision.
        ...(state.activeTabSize && {
          '--active-tab-width': `var(--humain-active-tab-width, ${state.activeTabSize.width}px)`,
          '--active-tab-height': `var(--humain-active-tab-height, ${state.activeTabSize.height}px)`,
        }),
        ...(typeof style === 'function' ? style(state) : style),
      })}
    />
  );
});

Tabs.displayName = 'Tabs';
TabsList.displayName = 'TabsList';
TabsTrigger.displayName = 'TabsTrigger';
TabsContent.displayName = 'TabsContent';
TabsIndicator.displayName = 'TabsIndicator';

export {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsTrigger,
  tabsListVariants,
  tabsTriggerVariants,
};
